import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ResidueType } from "../../types/residue.type";
import {
  Button,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Menu,
  message,
  Modal,
  Select,
  Space,
  Table,
  Typography,
  Switch,
} from "antd";
import { api } from "../../api/api";
import { DownOutlined } from "@ant-design/icons";
import { Currency } from "../../types/currency.type";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import { roundToThreeDecimals } from "../../utils/roundToThreeDecimals";
import { canEdit, hasAdminAccess } from "../../utils/roleUtils";
import { useAuth } from "../../contexts/auth.context";
import {useExchangeRate} from "../../contexts/exchangeRate.context";

const { Title } = Typography;
const { Search } = Input;

export default function Price() {
  const { authState } = useAuth();
  const { type } = useParams();
  const [data, setData] = useState<ResidueType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<ResidueType | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [form] = Form.useForm();
  const { loading: downloadLoading, downloadFile } = useDownloadFile();
  const [priceLoadingDetailed, setPriceLoadingDetailed] = useState(false);
  const [priceLoadingPretty, setPriceLoadingPretty] = useState(false);
  const {exchangeRate} = useExchangeRate();
  const [hasResidue, setHasResidue] = useState<boolean>(false);

  // Функция для получения средней себестоимости
  const getAverageCost = (item: ResidueType) => {
    return {
      averageCostUSD: item.averageCost?.averageCostUSD || 0,
      averageCostTJS: item.averageCost?.averageCostTJS || 0,
    };
  };


  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get("/residue", {
        params: {
          page,
          type,
          limit: pageSize,
          search: searchTerm,
          hasResidue: hasResidue ? 'true' : undefined,
        },
      });

      const { data, total } = response.data;

      setData(data);
      setTotal(total);
    } catch (error) {
      console.log(error);
      message.error("Ошибка получение остатка.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, pageSize, searchTerm, type, hasResidue]);

  const handleOpenModal = (item: ResidueType) => {
    setSelectedItem(item);
    form.setFieldsValue({
      amount: item.price[item.price.currency],
      currency: item.price.currency,
      exchangeRate: item.price.exchangeRate,
      lowTradePrice: item.lowTradePrice
        ? item.lowTradePrice[item.price.currency]
        : undefined,
      upTradePrice: item.upTradePrice
        ? item.upTradePrice[item.price.currency]
        : undefined,
    });
    setIsModalOpen(true);
  };

  const handleExport = () => {
    downloadFile("/export/residues", "residue_data.xlsx", { type });
  };

  const handleGetPriceDetailed = async () => {
    setPriceLoadingDetailed(true);
    try {
      await downloadFile("/price/list", `price_list_${type}.xlsx`, { 
        type,
        hasResidue: hasResidue ? 'true' : undefined,
      });
    } finally {
      setPriceLoadingDetailed(false);
    }
  };

  const handleGetPricePretty = async () => {
    setPriceLoadingPretty(true);
    try {
      await downloadFile("/price/list-pretty", `price_pretty_${type}.pdf`, { 
        type,
        hasResidue: hasResidue ? 'true' : undefined,
      });
    } finally {
      setPriceLoadingPretty(false);
    }
  };

  const columns = [
    {
      title: "Название",
      dataIndex: "name",
      key: "name",
      fixed: "left" as "left",
      width: 200,
    },
    {
      title: "Остаток",
      dataIndex: "residue",
      key: "residue",
      width: 100,
      render: (_: any, record: ResidueType) => {
        return record.residue !== undefined
          ? `${roundToThreeDecimals(record.residue)}`
          : "Н/Д";
      },
    },
    {
      title: "Цена USD",
      key: "priceUSD",
      width: 120,
      render: (_: any, record: ResidueType) => {
        return record.price && record.price.USD !== undefined
          ? `${roundToThreeDecimals(record.price.USD)}`
          : "Н/Д";
      },
    },
    {
      title: "Цена TJS",
      key: "priceTJS",
      width: 120,
      render: (_: any, record: ResidueType) => {
        return record.price && record.price.TJS !== undefined
          ? `${Math.round(record.price.TJS)}`
          : "Н/Д";
      },
    },
    {
      title: "Средняя себестоимость USD",
      key: "averageCostUSD",
      width: 180,
      render: (_: any, record: ResidueType) => {
        const { averageCostUSD } = getAverageCost(record);
        return averageCostUSD > 0 ? `${roundToThreeDecimals(averageCostUSD)}` : "Н/Д";
      },
    },
    {
      title: "Средняя себестоимость TJS",
      key: "averageCostTJS",
      width: 180,
      render: (_: any, record: ResidueType) => {
        const { averageCostTJS } = getAverageCost(record);
        return averageCostTJS > 0 ? `${roundToThreeDecimals(averageCostTJS)}` : "Н/Д";
      },
    },
    {
      title: "Цена мин USD",
      key: "lowTradePriceUSD",
      width: 120,
      render: (_: any, record: ResidueType) => {
        return record.lowTradePrice && record.lowTradePrice.USD !== undefined
          ? `${roundToThreeDecimals(record.lowTradePrice.USD)}`
          : "Н/Д";
      },
    },
    {
      title: "Цена мин TJS",
      key: "lowTradePriceTJS",
      width: 120,
      render: (_: any, record: ResidueType) => {
        return record.lowTradePrice && record.lowTradePrice.TJS !== undefined
          ? `${Math.round(record.lowTradePrice.TJS)}`
          : "Н/Д";
      },
    },
    {
      title: "Цена макс USD",
      key: "upTradePriceUSD",
      width: 120,
      render: (_: any, record: ResidueType) => {
        return record.upTradePrice && record.upTradePrice.USD !== undefined
          ? `${roundToThreeDecimals(record.upTradePrice.USD)}`
          : "Н/Д";
      },
    },
    {
      title: "Цена макс TJS",
      key: "upTradePriceTJS",
      width: 120,
      render: (_: any, record: ResidueType) => {
        return record.upTradePrice && record.upTradePrice.TJS !== undefined
          ? `${Math.round(record.upTradePrice.TJS)}`
          : "Н/Д";
      },
    },
    {
      title: "Валюта",
      key: "currency",
      width: 80,
      render: (_: any, record: ResidueType) => {
        return record.price && record.price.currency !== undefined
          ? `${record.price.currency}`
          : "Н/Д";
      },
    },
    {
      title: "",
      key: "actions",
      width: 100,
      fixed: "right" as "right",
      render: (_: any, record: ResidueType) => {
        const menu = (
          <Menu>
            {canEdit(authState.user?.role) && (
              <Menu.Item onClick={() => handleOpenModal(record)}>
                Изменить
              </Menu.Item>
            )}
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };


  const handleSubmitForm = async (values: {
    currency: Currency;
    amount: number;
    lowTradePrice: number;
    upTradePrice: number;
  }) => {
    setConfirmLoading(true);

    const data = {
      ...values,
      type,
      exchangeRate: exchangeRate
    };
    
    try {
      await api.post(`/price/${selectedItem?._id}`, data);

      message.success("Цены обновлены");
      form.resetFields();
      fetchData();
      setIsModalOpen(false);
      setConfirmLoading(false);
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error saving price:", error);
      message.error(error.response.data.error);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Title>Прайсы</Title>
      <Space style={{ marginBottom: 16 }} wrap>
        <Search
          placeholder="Поиск по названию"
          onSearch={handleSearch}
          allowClear
          style={{ width: 300 }}
        />

        <Space>
          <span>Имеет остаток:</span>
          <Switch
            checked={hasResidue}
            onChange={setHasResidue}
            checkedChildren="Да"
            unCheckedChildren="Нет"
          />
        </Space>

        <Button
          type="default"
          loading={downloadLoading}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleExport}
        >
          Экспорт в Excel
        </Button>
        <Button
          type="default"
          loading={priceLoadingPretty}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleGetPricePretty}
        >
          Прайс
        </Button>
        {hasAdminAccess(authState.user?.role) && (
          <Button
            type="default"
            loading={priceLoadingDetailed}
            icon={<PiMicrosoftExcelLogoFill />}
            onClick={handleGetPriceDetailed}
          >
            Прайс (подробный)
          </Button>
        )}
      </Space>

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey={(record) => record._id}
        pagination={{
          current: page,
          total,
          pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
        }}
        scroll={{ x: "max-content" }}
      />

      <Modal
        title="Изменить"
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedItem(null);
        }}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} onFinish={handleSubmitForm} layout="vertical">
          <Form.Item
            name="currency"
            label="Валюта"
            rules={[{ required: true, message: "Выберите валюту" }]}
          >
            <Select placeholder="Выберите валюту">
              {Object.values(Currency).map((currency) => (
                <Select.Option key={currency} value={currency}>
                  {currency}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="amount"
            label="Цена"
            rules={[{ required: true, message: "Введите цену" }]}
          >
            <InputNumber min={0} />
          </Form.Item>

          <Form.Item
            name="lowTradePrice"
            label="Цена торговли от"
            rules={[{ required: true, message: "Введите цену" }]}
          >
            <InputNumber min={0} />
          </Form.Item>

          <Form.Item
            name="upTradePrice"
            label="Цена торговли до"
          >
            <InputNumber min={0} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
